import React, { useEffect, useMemo, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import Reveal from "../components/Reveal";
import { IconArrowRight, IconChat, IconMail, IconStore, IconWallet } from "../components/Icons";
import { SHOW_MERCHANT_PLANS, WEBAPP_URL } from "../siteConfig";
import { useDocumentMeta } from "../useDocumentMeta";
import { getRecaptchaToken, loadRecaptchaV3 } from "../recaptcha";
import "../styles/contact.css";

const ContactSalesPage = () => {
  // API endpoint to receive the contact form.
  // Set per build in .env.production; the fallback is the production endpoint.
  const contactApiUrl =
    process.env.REACT_APP_CONTACT_ENDPOINT || "https://pabs.online/api/v1/contact";

  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

  useDocumentMeta({
    title: "Contact PABS — Sales, support, and partnerships",
    description:
      "Get in touch with the PABS team. Reach out about listing your store, becoming a rider, or general questions about our food, grocery, Pasugo, and Angkas services.",
    canonicalPath: "/contact",
    ogImage: "https://pabs.online/logo512.png",
    jsonLdId: "pabs-contact-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact PABS",
      url: "https://pabs.online/contact",
    },
  });

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [privacyOk, setPrivacyOk] = useState(false);

  const [recaptchaError, setRecaptchaError] = useState<string>("");

  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string>("");

  const canSubmit = useMemo(() => {
    if (!privacyOk) return false;
    if (!fullName.trim() || !email.trim()) return false;
    if (message.trim().length < 10) return false;
    if (!siteKey) return false;
    return true;
  }, [privacyOk, fullName, email, message, siteKey]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!siteKey) return;

      // Preload so the first submit works; the token itself is fetched on submit.
      try {
        await loadRecaptchaV3(siteKey);
      } catch (e: any) {
        if (!cancelled) setRecaptchaError(e?.message || "reCAPTCHA failed to load.");
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");
    setInfoMessage("");

    if (!siteKey) {
      setSubmitError(
        "Contact form is not configured: missing reCAPTCHA site key.",
      );
      return;
    }

    setSubmitting(true);
    setInfoMessage("Submitting your message to the PABS support team…");
    try {
      if (!contactApiUrl) {
        setSubmitError(
          "Contact form is not configured: missing REACT_APP_API_BASE (or REACT_APP_CONTACT_API_URL override).",
        );
        return;
      }

      // reCAPTCHA v3 token (no widget). Waits for reCAPTCHA if still loading.
      const recaptchaToken = await getRecaptchaToken(siteKey);
      setRecaptchaError("");

      const res = await fetch(contactApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName,
          email,
          message,
          recaptchaToken,
          recaptchaAction: "contact",
          source: "Website",
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to send. Please try again in a moment.");
      }

      setInfoMessage("");
      setSubmitSuccess(
        `Thanks for reaching out to PABS!\n\nWe've received your message and truly appreciate you getting in touch. Our team will review your request and respond within 1 - 2 business days. In the meantime, feel free to explore the Help Center for quick answers and resources.`,
      );

      setFullName("");
      setEmail("");
      setMessage("");
      setPrivacyOk(false);
    } catch (err: any) {
      setSubmitError(
        err?.message || "Something went wrong while sending your message.",
      );

      setInfoMessage(
        "If the issue continues, you may contact support through the help center or try submitting the form again later.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const messageTooShort = Boolean(message) && message.trim().length < 10;

  return (
    <>
      <SiteHeader current="/contact" />

      <main id="main" className="ct">
        <section className="ct__hero">
          <div className="ct__bg" aria-hidden="true" />
          <div className="u-container">
            <Reveal className="ct__head">
              <span className="t-eyebrow">Contact</span>
              <h1 className="t-h1 ct__title">
                Talk to the <span className="t-grad">PABS team.</span>
              </h1>
              <p className="t-lead ct__lead">
                Send a message and we'll route it to the right team. For order or
                delivery issues, use the in-app help center when available.
              </p>
            </Reveal>

            <div className="ct__layout">
              {/* ---- Aside: routes and expectations ---- */}
              <Reveal className="ct__aside">
                <div className="ct__panel">
                  <h2 className="ct__panel-title">Get there faster</h2>
                  <ul className="ct__links">
                    <li>
                      <a className="ct__link" href={WEBAPP_URL}>
                        <span className="ct__link-icon">
                          <IconChat size={19} />
                        </span>
                        <span className="ct__link-text">
                          <b>Help Center</b>
                          <small>Support</small>
                        </span>
                        <IconArrowRight size={17} className="ct__link-arrow" />
                      </a>
                    </li>
                    {/* Subscriptions exist only for merchants on the Business plan. */}
                    {SHOW_MERCHANT_PLANS ? (
                    <li>
                      <a className="ct__link" href={WEBAPP_URL}>
                        <span className="ct__link-icon">
                          <IconWallet size={19} />
                        </span>
                        <span className="ct__link-text">
                          <b>Billing</b>
                          <small>Manage subscription</small>
                        </span>
                        <IconArrowRight size={17} className="ct__link-arrow" />
                      </a>
                    </li>
                    ) : null}
                    <li>
                      <a className="ct__link" href={WEBAPP_URL}>
                        <span className="ct__link-icon">
                          <IconStore size={19} />
                        </span>
                        <span className="ct__link-text">
                          <b>Account</b>
                          <small>Settings</small>
                        </span>
                        <IconArrowRight size={17} className="ct__link-arrow" />
                      </a>
                    </li>
                  </ul>

                  <div className="ct__sla">
                    <IconMail size={18} />
                    <span>Typical response time: 1–2 business days.</span>
                  </div>
                </div>

                <a className="ct__faq" href="/faq">
                  <div>
                    <b>Looking for a quick answer?</b>
                    <small>Browse 30+ answers in the PABS FAQ.</small>
                  </div>
                  <IconArrowRight size={18} />
                </a>
              </Reveal>

              {/* ---- Form ---- */}
              <Reveal delay={100} className="ct__formwrap">
                <form className="ct__form" onSubmit={onSubmit} noValidate={false}>
                  <div className="ct__row">
                    <div className="field">
                      <label htmlFor="fullName">Full name</label>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        autoComplete="name"
                        placeholder="Jane Doe"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="field">
                      <label htmlFor="email">Email</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="jane@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      placeholder="Tell us what you're trying to accomplish and any relevant details."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      minLength={10}
                      required
                      aria-invalid={messageTooShort}
                      aria-describedby="messageHelp"
                    />
                    <div className="helper" id="messageHelp">
                      Please don't include passwords or sensitive information.
                    </div>
                    {messageTooShort ? (
                      <div className="error" role="alert">
                        Message must be at least 10 characters.
                      </div>
                    ) : null}
                  </div>

                  <label className="ct__check" htmlFor="privacyOk">
                    <input
                      id="privacyOk"
                      type="checkbox"
                      checked={privacyOk}
                      onChange={(e) => setPrivacyOk(e.target.checked)}
                      required
                    />
                    <span>
                      I agree to the <a href="/privacy">Privacy Statement</a>.
                    </span>
                  </label>

                  {!siteKey ? (
                    <div className="error" role="alert">
                      Missing reCAPTCHA site key. Set{" "}
                      <code>REACT_APP_RECAPTCHA_SITE_KEY</code>.
                    </div>
                  ) : null}

                  {recaptchaError ? (
                    <div className="error" role="alert">
                      {recaptchaError}
                    </div>
                  ) : null}

                  {submitError ? (
                    <div className="error" role="alert">
                      {submitError}
                    </div>
                  ) : null}

                  {submitSuccess ? (
                    <div className="success" role="status">
                      {submitSuccess}
                    </div>
                  ) : null}

                  {infoMessage ? (
                    <div className="notice" role="status">
                      {infoMessage}
                    </div>
                  ) : null}

                  <div className="ct__actions">
                    <button
                      className="btn btn--primary btn--lg"
                      type="submit"
                      disabled={!canSubmit || submitting}
                    >
                      {submitting ? "Sending…" : "Send message"}
                      {submitting ? null : (
                        <IconArrowRight
                          size={18}
                          className="btn__icon btn__icon--arrow"
                        />
                      )}
                    </button>
                  </div>

                  <p className="ct__recaptcha">
                    Protected by reCAPTCHA — Google{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Service
                    </a>{" "}
                    apply.
                  </p>
                </form>
              </Reveal>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ContactSalesPage;
